/**
 * Fetches real BluePulse data (kelp cells, SST vectors) and converts them to
 * OrbitalDataLayers for the Tier 3 OrbitalScene.
 *
 * Uses static Pacific Coast bbox — the orbital view is always centered on
 * the California / Baja kelp belt and surrounding Pacific.
 */
import { useEffect, useState } from "react";
import { fetchKelpGeojson } from "../lib/kelpApi";
import { fetchLayerVectorField } from "../lib/layersApi";

export type OrbitalPoint = { lat: number; lon: number; coverage: number };
export type SSTSample = { lat: number; lon: number; tempC: number };

export interface OrbitalLayers {
  kelp: OrbitalPoint[];
  sst: SSTSample[];
  loading: boolean;
}

// California / Baja Pacific coast kelp belt
const PACIFIC_BBOX: [number, number, number, number] = [-126, 22, -106, 42];

export function useOrbitalDataLayers(): OrbitalLayers {
  const [kelp, setKelp] = useState<OrbitalPoint[]>([]);
  const [sst, setSst] = useState<SSTSample[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    async function load() {
      try {
        // Kelp: most recent available date, zoom 5 (gives ~8km resolution)
        const today = new Date().toISOString().split("T")[0];
        const kelpResult = await fetchKelpGeojson({
          minLon: PACIFIC_BBOX[0],
          minLat: PACIFIC_BBOX[1],
          maxLon: PACIFIC_BBOX[2],
          maxLat: PACIFIC_BBOX[3],
          zoom: 5,
          date: today,
          signal: controller.signal,
        });

        if (!cancelled) {
          const pts: OrbitalPoint[] = [];
          for (const f of kelpResult.features) {
            const cov = f.properties?.coverage ?? f.properties?.kelp_fraction ?? 0;
            if (!cov) continue;
            // Use centroid of polygon
            const ring = (f as any).geometry?.coordinates?.[0] as [number, number][];
            if (!ring?.length) continue;
            const lon = ring.reduce((s: number, p: [number, number]) => s + p[0], 0) / ring.length;
            const lat = ring.reduce((s: number, p: [number, number]) => s + p[1], 0) / ring.length;
            pts.push({ lat, lon, coverage: Number(cov) });
          }
          setKelp(pts.slice(0, 64)); // shader max
        }

        // SST: vector field (use as temperature proxy)
        const sstResult = await fetchLayerVectorField({
          layerId: "sst",
          bbox: [-150, 15, -100, 55],
          time: "latest",
          maxPoints: 32,
          signal: controller.signal,
        });

        if (!cancelled) {
          const samples: SSTSample[] = sstResult.features.map((f) => ({
            lon: f.geometry.coordinates[0],
            lat: f.geometry.coordinates[1],
            tempC: Math.abs(f.properties.speed ?? 18),
          }));
          setSst(samples.slice(0, 32));
        }
      } catch {
        // Graceful fallback — orbital still renders without data
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, []);

  return { kelp, sst, loading };
}
